"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TitledShade = exports.ColorPair = exports.PropertyTitledShade = exports.PropertyColorPair = exports.PropertyColorShade = exports.PropertyPixelRange = exports.PropertyFontRange = exports.PropertyPercentage = exports.PropertyNumberRange = exports.PropertyFontFamily = exports.defaultSecondaryFont = exports.defaultPrimaryFont = exports.PropertyBackgroundColorStyle = exports.PropertyButtonText = exports.PropertyShadowSelectable = exports.PropertyStringCategorySelectable = exports.PropertyIndexSelectable = exports.PropertyPixelSelectable = exports.PropertyPercentageSelectable = exports.PropertyNumberSelectable = exports.PropertyStringSelectable = exports.PropertySelectable = exports.PropertyRange = exports.PropertyTime = exports.PropertyPixel = exports.PropertyBoolean = exports.PropertyNumber = exports.PropertyString = exports.PropertyGroupListener = exports.Property = void 0;
/*
 * Copyright (c) 2023 Discover Financial Services
 * Licensed under MIT License. See License.txt in the project root for license information
 */
const node_1 = require("./node");
const interfaces_1 = require("../interfaces");
const shade_1 = require("./shade");
const logger_1 = require("../util/logger");
const log = new logger_1.Logger("prop");
/**
 * A property of a generic type.
 * @category Utilities
 */
class Property extends node_1.Node {
    constructor(name, required, parent, opts) {
        super(name, parent);
        this.required = required;
        this.opts = opts || {};
        parent.addProperty(this);
    }
    isInitialized() {
        if (!this.required || this.getValue() !== undefined) {
            return true;
        }
        else {
            return false;
        }
    }
    getValue() {
        if (this.value !== undefined) {
            return this.value;
        }
        return this.getDefaultValue();
    }
    setValue(newValue) {
        const oldValue = this.value;
        if (oldValue !== newValue) {
            log.debug(`Changing value of ${this.key} from ${oldValue} to ${JSON.stringify(newValue)}`);
            // Get the old node state before changing the property value
            const oldNodeState = this.getNodeState();
            // Change the property value
            this.value = newValue;
            // Send the value change event
            this.sendValueChangeNotification(oldValue, newValue);
            // Compare the current node state to the old state and send
            // any node enabled/disabled events which should be sent.
            this.compareNodeState(oldNodeState);
        }
    }
    sendValueChangeNotification(oldValue, newValue) {
        const event = {
            type: interfaces_1.EventType.ValueChanged,
            node: this,
            oldValue,
            newValue,
        };
        this.notifyListeners(event);
    }
    getDefaultValue() {
        if (this.opts.getDefaultValue) {
            return this.opts.getDefaultValue();
        }
        else {
            return this.opts.defaultValue;
        }
    }
    setListener(name, listener, eventTypes) {
        const ls = super.setListener(name, listener, eventTypes);
        const val = this.getValue();
        if (val !== undefined) {
            // Go ahead and notify the listener of the current value
            const event = {
                type: interfaces_1.EventType.ValueChanged,
                node: this,
                oldValue: undefined,
                newValue: val,
            };
            listener(event);
        }
        return ls;
    }
    setPropertyListener(name, callback) {
        return this.setListener(name, callback, [interfaces_1.EventType.ValueChanged]);
    }
    serialize() {
        const obj = this.value;
        if (obj && typeof obj.serialize === 'function') {
            return obj.serialize();
        }
        return this.value;
    }
    deserialize(obj) {
        this.value = obj;
    }
    toJSON() {
        return { value: this.value };
    }
    getShadeRef(shade) {
        if (!shade)
            return undefined;
        if (shade.coreShadeName)
            return { coreShadeName: shade.coreShadeName };
        if (shade.key)
            return { key: shade.key };
        return { hex: shade.hex, opacity: shade.opacity, mode: shade.hasMode() ? shade.getMode().key : undefined };
    }
    getShadeFromRef(ref) {
        if (!ref)
            return undefined;
        if (ref.coreShadeName)
            return shade_1.Shade.getCoreShade(ref.coreShadeName);
        if (ref.key)
            return this.getDesignSystem().findShade(ref.key);
        if (ref.hex) {
            const shade = shade_1.Shade.fromHex(ref.hex);
            if (ref.opacity)
                shade.setOpacity(ref.opacity);
            if (ref.mode) {
                const mode = this.getDesignSystem().getNode(ref.mode);
                if (!mode)
                    throw new Error(`Mode node was not found at ${ref.mode}`);
                shade.setMode(mode);
            }
            return shade;
        }
        throw new Error(`Invalid shade reference: ${JSON.stringify(ref)}`);
    }
    _addProperties(props) {
        props.push(this);
    }
}
exports.Property = Property;
/**
 * Register a single listener for a group of properties and call the callback
 * when all of the properties have values and any of their values change.
 * @category Utilities
 */
class PropertyGroupListener {
    constructor(name, props, cb) {
        this.started = false;
        this.props = props;
        this.cb = cb;
        this.lname = `_tb.PropertyGroupListener.${name}`;
        this.start();
    }
    start() {
        log.debug(`Starting property group listener for ${this.lname}`);
        this.props.forEach(prop => {
            prop.setListener(this.lname, this.callback.bind(this));
        });
        this.started = true;
        if (this.allPropsHaveValues()) {
            this.cb(this);
        }
    }
    stop() {
        log.debug(`Stopping property group listener for ${this.lname}`);
        this.props.forEach(prop => prop.removeListener(this.lname));
        this.started = false;
    }
    callback(event) {
        if (this.started && this.allPropsHaveValues()) {
            log.debug(`Calling the listener for property group ${this.lname} because it is ready`);
            this.cb(this);
        }
    }
    allPropsHaveValues() {
        for (let i = 0; i < this.props.length; i++) {
            const p = this.props[i];
            if (p.getValue() === undefined) {
                log.debug(`Property group ${this.lname} is not ready because property ${p.key} has no value`);
                return false;
            }
        }
        return true;
    }
}
exports.PropertyGroupListener = PropertyGroupListener;
class PropertyString extends Property {
}
exports.PropertyString = PropertyString;
class PropertyNumber extends Property {
}
exports.PropertyNumber = PropertyNumber;
class PropertyBoolean extends Property {
}
exports.PropertyBoolean = PropertyBoolean;
class PropertyPixel extends Property {
}
exports.PropertyPixel = PropertyPixel;
class PropertyTime extends Property {
}
exports.PropertyTime = PropertyTime;
/**
 * A range of numeric values property.
 * @category Utilities
 */
class PropertyRange extends Property {
    constructor(name, min, max, required, parent, opts) {
        super(name, required, parent, opts);
        this.min = min;
        this.max = max;
    }
    deserialize(obj) {
        this.min = obj.min;
        this.max = obj.max;
    }
    serialize() {
        return {
            min: this.min,
            max: this.max,
        };
    }
}
exports.PropertyRange = PropertyRange;
class PropertySelectable extends Property {
    constructor(name, required, parent, opts) {
        super(name, required, parent, opts);
        this.sopts = opts;
        if (!opts.getSelectables && !opts.selectables) {
            throw new Error(`Either 'getSelectables' or 'selectables' must be defined for ${this.key}`);
        }
        const dp = opts.dependentProps;
        if (dp) {
            dp.forEach(prop => this.addDependency(prop));
            this.propertyGroupListener = new PropertyGroupListener(this.key, dp, this.sendSelectablesChangedNotification.bind(this));
        }
    }
    setDefault(defaultValue) {
        this.sopts.defaultValue = defaultValue;
    }
    stop() {
        if (this.propertyGroupListener) {
            this.propertyGroupListener.stop();
            this.propertyGroupListener = undefined;
        }
    }
    getSelectableValues() {
        if (this.sopts.getSelectables) {
            return this.sopts.getSelectables();
        }
        else if (this.sopts.selectables) {
            return this.sopts.selectables;
        }
        else {
            throw new Error(`Should be unreachable`);
        }
    }
    setSelectableValues(s) {
        if (this.sopts.getSelectables) {
            throw new Error(`Can't call setSelectableValues if function getSelectables is set`);
        }
        else if (this.sopts.selectables) {
            this.sopts.selectables = s;
            this.sendSelectablesChangedNotification();
        }
        else {
            throw new Error(`Should be unreachable`);
        }
    }
    getDefaultValue() {
        if (this.sopts.getDefaultValue) {
            return this.sopts.getDefaultValue();
        }
        else if (this.sopts.defaultValue !== undefined) {
            return this.sopts.defaultValue;
        }
        else {
            return undefined;
        }
    }
    sendSelectablesChangedNotification() {
        this.notifyListeners({ type: interfaces_1.EventType.SelectablesChanged, node: this });
    }
}
exports.PropertySelectable = PropertySelectable;
class PropertyStringSelectable extends PropertySelectable {
}
exports.PropertyStringSelectable = PropertyStringSelectable;
class PropertyNumberSelectable extends PropertySelectable {
    constructor(name, required, parent, numbers, defaultValue) {
        super(name, required, parent, { selectables: numbers, defaultValue });
    }
}
exports.PropertyNumberSelectable = PropertyNumberSelectable;
class PropertyPercentageSelectable extends PropertyNumberSelectable {
    constructor(name, required, parent, percentages, defaultPercentage) {
        super(name, required, parent, percentages, defaultPercentage);
    }
}
exports.PropertyPercentageSelectable = PropertyPercentageSelectable;
class PropertyPixelSelectable extends PropertySelectable {
    constructor(name, required, parent, pixels, defaultValue) {
        super(name, required, parent, { selectables: pixels, defaultValue });
    }
}
exports.PropertyPixelSelectable = PropertyPixelSelectable;
class PropertyIndexSelectable extends PropertyStringSelectable {
    constructor(name, required, parent, min, max, none, prefix, inversePrefix, defIdx) {
        super(name, required, parent, {
            selectables: PropertyIndexSelectable.toStrings(min, max, none, prefix, inversePrefix),
            defaultValue: PropertyIndexSelectable.indexToString(defIdx || 0, none, prefix, inversePrefix),
        });
        this.none = none;
        this.prefix = prefix;
        this.inversePrefix = inversePrefix;
    }
    toIndex() {
        const val = this.getValue() || this.none;
        if (val === this.none)
            return 0;
        if (val.startsWith(this.prefix))
            return parseInt(val.substring(this.prefix.length));
        if (val.startsWith(this.inversePrefix))
            return -parseInt(val.substring(this.inversePrefix.length));
        throw new Error(`Invalid ${this.name} value: '${val}'`);
    }
    static indexToString(idx, none, prefix, inversePrefix) {
        if (idx == 0)
            return none;
        if (idx > 0)
            return `${prefix}${idx}`;
        return `${inversePrefix}${-idx}`;
    }
    static toStrings(min, max, none, prefix, inversePrefix) {
        const strs = [none];
        for (let i = 1; i <= max; i++)
            strs.push(PropertyIndexSelectable.indexToString(i, none, prefix, inversePrefix));
        for (let i = 1; i <= min; i++)
            strs.push(PropertyIndexSelectable.indexToString(-i, none, prefix, inversePrefix));
        return strs;
    }
}
exports.PropertyIndexSelectable = PropertyIndexSelectable;
class PropertyStringCategorySelectable extends PropertySelectable {
    constructor(name, required, parent, opts) {
        super(name, required, parent, opts);
    }
    getCategoryAndIndex() {
        const value = this.getValue();
        if (!value)
            return undefined;
        const sv = this.getSelectableValues();
        for (let categoryIndex = 0; categoryIndex < sv.length; categoryIndex++) {
            const category = sv[categoryIndex];
            if (category.display === value) {
                return { category, categoryIndex, memberIndex: -1 };
            }
            const mems = category.members;
            for (let memberIndex = 0; memberIndex < mems.length; memberIndex++) {
                if (mems[memberIndex] === value) {
                    return { category, categoryIndex, memberIndex };
                }
            }
        }
        return undefined;
    }
}
exports.PropertyStringCategorySelectable = PropertyStringCategorySelectable;
class PropertyShadowSelectable extends PropertyStringCategorySelectable {
    constructor(name, required, parent, opts) {
        super(name, required, parent, {
            selectables: [
                newStringCategory("None", "", "", "", 0),
                newStringCategory("Elevations", "Elevation", "elevation", "Elevation", 9),
                newStringCategory("Bevels", "Bevel", "bevel", "Bevel", 9),
                newStringCategory("Inset Bevels", "Inset Bevel", "inset-bevel", "InsetBevel", 9),
                newStringCategory("Grooves", "Groove", "groove", "Groove", 9),
                newStringCategory("Ridges", "Ridge", "ridge", "Ridge", 9),
                newStringCategory("Recesses", "Recess", "recess", "Recess", 9),
                newStringCategory("Glows", "Glow", "glow", "Glow", 8),
            ],
            defaultValue: opts ? opts.defaultValue : "None"
        });
    }
}
exports.PropertyShadowSelectable = PropertyShadowSelectable;
class PropertyButtonText extends PropertyStringSelectable {
    constructor(name, required, node) {
        super(name, required, node, { selectables: ["CTA LARGE", "CTA Small"] });
    }
}
exports.PropertyButtonText = PropertyButtonText;
class PropertyBackgroundColorStyle extends PropertyStringSelectable {
    constructor(name, required, node) {
        super(name, required, node, { selectables: ["Solid", "Opaque", "Gradient"] });
    }
}
exports.PropertyBackgroundColorStyle = PropertyBackgroundColorStyle;
exports.defaultPrimaryFont = "Open Sans";
exports.defaultSecondaryFont = "Open Sans";
class PropertyFontFamily extends PropertyStringSelectable {
    constructor(name, required, node) {
        super(name, required, node, { selectables: [exports.defaultPrimaryFont, exports.defaultSecondaryFont], defaultValue: exports.defaultPrimaryFont });
    }
}
exports.PropertyFontFamily = PropertyFontFamily;
class PropertyNumberRange extends PropertyNumber {
    constructor(name, required, node, min, max, def) {
        super(name, required, node, { defaultValue: def });
        this.min = min;
        this.max = max;
    }
    setValue(num) {
        if (num < this.min || num > this.max) {
            throw new Error(`Can't set value to ${num} for property '${this.key}' which has a range of [${this.min},${this.max}]`);
        }
        super.setValue(num);
    }
}
exports.PropertyNumberRange = PropertyNumberRange;
class PropertyPercentage extends PropertyNumberRange {
    constructor(name, required, parent, defaultPercentage) {
        super(name, required, parent, 0, 100, defaultPercentage);
    }
}
exports.PropertyPercentage = PropertyPercentage;
class PropertyFontRange extends PropertyNumberRange {
}
exports.PropertyFontRange = PropertyFontRange;
class PropertyPixelRange extends PropertyNumberRange {
}
exports.PropertyPixelRange = PropertyPixelRange;
class PropertyColorShade extends PropertySelectable {
    /*
     * All PropertyColorShade objects are references to Shade objects built by the color palette.
     * The info in the serialization is the key to lly serializing a reference to the
     * shade so that it be looked up.  The reason is that a sh
     */
    serialize() {
        return this.getShadeRef(this.value);
    }
    deserialize(obj) {
        this.value = this.getShadeFromRef(obj);
    }
}
exports.PropertyColorShade = PropertyColorShade;
class PropertyColorPair extends PropertySelectable {
    constructor(name, required, theme, selectables) {
        super(name, required, theme, { selectables });
    }
    serialize() {
        if (!this.value)
            return undefined;
        return {
            title: this.value.title,
            primary: this.getShadeRef(this.value.secondary),
            secondary: this.getShadeRef(this.value.primary),
            lighter: this.value.lighter,
        };
    }
    deserialize(obj) {
        if (!obj)
            return;
        this.value = new ColorPair(obj.title, this.getShadeFromRef(obj.primary), this.getShadeFromRef(obj.secondary), obj.lighter);
    }
}
exports.PropertyColorPair = PropertyColorPair;
class PropertyTitledShade extends PropertySelectable {
    serialize() {
        if (!this.value)
            return undefined;
        return {
            title: this.value.title,
            shade: this.getShadeRef(this.value.shade),
        };
    }
    deserialize(obj) {
        if (!obj)
            return;
        this.value = new TitledShade(obj.title, this.getShadeFromRef(obj.shade));
    }
}
exports.PropertyTitledShade = PropertyTitledShade;
class ColorPair {
    constructor(title, primary, secondary, lighter) {
        this.title = title;
        this.primary = primary;
        this.secondary = secondary;
        this.lighter = lighter;
    }
    deserialize(obj) {
        if (!obj)
            return;
        this.title = obj.title;
        this.primary.deserialize(obj.primary);
        this.secondary.deserialize(obj.secondary);
        this.lighter = obj.lighter;
    }
    serialize() {
        const obj = {};
        obj.title = this.title;
        obj.primary = this.primary;
        obj.secondary = this.secondary.serialize();
        obj.lighter = this.lighter;
        return obj;
    }
}
exports.ColorPair = ColorPair;
class TitledShade {
    constructor(title, shade) {
        this.title = title;
        this.shade = shade;
    }
    deserialize(obj) {
        if (!obj)
            return;
        this.shade.deserialize(obj.shade);
    }
    serialize() {
        const obj = {};
        obj.shade = this.shade.serialize();
        return obj;
    }
}
exports.TitledShade = TitledShade;
function newStringCategory(display, prefix, css, json, count) {
    const members = [];
    for (let i = 1; i <= count; i++) {
        members.push(`${prefix} ${i}`);
    }
    return { display, css, json, members };
}
